/**
 * Utility functions for handling image URLs
 * Provides consistent processing for image paths and URLs
 */

/**
 * Sanitizes an image URL by ensuring proper encoding for browser compatibility.
 * Handles cases where URLs might contain spaces or unencoded special characters.
 * Preserves already-encoded parts and doesn't double-encode.
 * 
 * @param {string} url - The image URL to sanitize
 * @returns {string} The sanitized image URL
 */
/**
 * Returns true if the URL is hosted on a Shopify CDN.
 * Shopify image URLs must not be re-encoded — the path segments contain
 * numeric store/product IDs that are already valid and will break if modified.
 */
const isShopifyImageUrl = (url) => {
    return /cdn\.shopify\.com/i.test(url);
};

export const sanitizeImageUrl = (url) => {
    if (!url || typeof url !== 'string') return '';

    // Trim whitespace
    url = url.trim();

    // If empty after trim, return empty
    if (!url) return '';

    // Shopify CDN URLs contain store/product IDs in the path.
    // Encoding these segments corrupts the URL — return as-is.
    if (isShopifyImageUrl(url)) return url;

    try {
        // Check if it's a full URL with protocol
        const protocolMatch = url.match(/^(https?:\/\/)/i);

        if (protocolMatch) {
            // It's a full URL - manually parse it to avoid URL constructor's query string parsing
            const protocol = protocolMatch[1];
            const afterProtocol = url.substring(protocol.length);

            // Find the first / which marks the start of the pathname
            const firstSlashIndex = afterProtocol.indexOf('/');

            let hostname, pathname;
            if (firstSlashIndex === -1) {
                // No pathname, just hostname
                hostname = afterProtocol;
                pathname = '';
            } else {
                hostname = afterProtocol.substring(0, firstSlashIndex);
                pathname = afterProtocol.substring(firstSlashIndex);
            }

            // Encode the pathname properly by encoding each segment
            // Split by /, encode each part, then rejoin
            const encodedPathSegments = pathname.split('/').map(segment => {
                // Don't encode empty segments (from leading/trailing slashes)
                if (!segment) return segment;

                // Decode first in case it's partially encoded, then encode properly
                try {
                    const decoded = decodeURIComponent(segment);
                    return encodeURIComponent(decoded);
                } catch (e) {
                    // If decode fails, just encode as-is
                    return encodeURIComponent(segment);
                }
            });

            const encodedPath = encodedPathSegments.join('/');

            // Reconstruct the URL
            return protocol + hostname + encodedPath;
        } else {
            // Relative path - encode it as a path
            const segments = url.split('/').map(segment => {
                if (!segment) return segment;

                try {
                    const decoded = decodeURIComponent(segment);
                    return encodeURIComponent(decoded);
                } catch (e) {
                    return encodeURIComponent(segment);
                }
            });

            return segments.join('/');
        }
    } catch (error) {
        console.warn('Error sanitizing image URL:', error);
        // Return original URL if all encoding attempts fail
        return url;
    }
};
